import { Hotkey, isEventKey, isHotKey, KeyCode } from "../dom";
import { EL_ID_PORTAL_MAIN_AFTER } from "../../components/Portal/PortalMainAfter/constants";

const part = ":not([disabled]):not([data-focus='false'])";

export const selectorFocusableElements =
  "input" + part + ","
  + "[data-submit]" + part + ","
  + "button" + part + ","
  + "a" + part + ","
  + "select" + part + ","
  + "textarea" + part + ","
  + "[tabindex]" + part;

export const handleKeyDownNavigation = (container, e, stopPropagation, prevNextX = true) => {
  if (!container || !e) {
    return;
  }

  let isKeyCodeItemPrev = isHotKey(e, Hotkey.itemPrevY)
  if (prevNextX && !isKeyCodeItemPrev) {
    isKeyCodeItemPrev = isHotKey(e, Hotkey.itemPrevX);
  }

  let isKeyCodeItemNext = isHotKey(e, Hotkey.itemNextY)
  if (prevNextX && !isKeyCodeItemNext) {
    isKeyCodeItemNext = isHotKey(e, Hotkey.itemNextX);
  }

  if (isKeyCodeItemPrev || isKeyCodeItemNext) {
    const focusableElements = container.querySelectorAll(selectorFocusableElements);

    let indexItemCurrent = -1;

    const documentActiveElement = document.activeElement;
    for (let i = 0; i < focusableElements.length; i++) {
      if (focusableElements[i] === documentActiveElement) {
        indexItemCurrent = i;
        break;
      }
    }

    let indexItemNew;

    if (indexItemCurrent === -1) {
      indexItemNew = isKeyCodeItemNext ? 0 : focusableElements.length - 1;
    } else if (isKeyCodeItemPrev) {
      if (indexItemCurrent < 1) {
        indexItemNew = focusableElements.length - 1;
      } else {
        indexItemNew = indexItemCurrent - 1;
      }
    } else {
      if (indexItemCurrent > focusableElements.length - 2) {
        indexItemNew = 0;
      } else {
        indexItemNew = indexItemCurrent + 1;
      }
    }

    if (indexItemNew !== undefined && focusableElements[indexItemNew]) {
      focusableElements[indexItemNew].focus();

      e.preventDefault();

      if (stopPropagation) {
        e.stopPropagation();
      }
    }
  }
};

const handleKeyDown = (e, hotkey, cb) => {
  if (e && hotkey && cb && isHotKey(e, hotkey)) {
    cb(e);

    e.preventDefault();

    return true;
  }

  return false;
};

export const handleKeyDownSelectItem = (e, cb) => handleKeyDown(e, Hotkey.selectButtonOrLink, cb);

export const handleKeyDownOpenItem = (e, cb) => handleKeyDown(e, Hotkey.openItem, cb);

export const handleKeyDownExpandItem = (e, cb) => handleKeyDown(e, Hotkey.expandItem, cb);

export const handleKeyDownCheckItem = (e, cb) => handleKeyDown(e, Hotkey.checkItem, cb);

export const handleKeyDownContextMenuItem = (e, cb) => handleKeyDown(e, Hotkey.itemContextMenu, cb);

export const handleKeyDownExitFloatWindow = (e, cb) => handleKeyDown(e, Hotkey.exitFloatWindow, cb);

export const isTryingExitElementByButtonTab = (element, e) => {
  if (!element || !e) {
    return false;
  }

  return isEventKey(e, KeyCode.tab) && (
    !e.shiftKey && lastElementIsFocusable(element)
    || e.shiftKey && firstElementIsFocusable(element)
  );
};

export const handleItemKeyDown = (e, onCheck, onExpand, onOpen, onContextMenu) => {
  if (!e) {
    return false;
  }

  return handleKeyDownCheckItem(e, onCheck)
    || handleKeyDownExpandItem(e, onExpand)
    || handleKeyDownContextMenuItem(e, onContextMenu)
    || handleKeyDownOpenItem(e, onOpen);
};

export const lastElementIsFocusable = (target = null, activeElement = document.activeElement) => {
  if (!target) {
    return false;
  }

  const elements = target.querySelectorAll(selectorFocusableElements);

  const filteredElements = elements && Array.from(elements).filter(filterVisibleElements);

  if (activeElement && filteredElements && filteredElements.length) {
    return activeElement === filteredElements[filteredElements.length - 1];
  }

  return false;
};

export const filterVisibleElements = (element) => {
  return element.offsetParent !== null && !element.closest(".hideView");
};

export const firstElementIsFocusable = (target = null, activeElement = document.activeElement) => {
  if (!target) {
    return false;
  }

  const elements = target.querySelectorAll(selectorFocusableElements);
  const filteredElements = elements && Array.from(elements).filter(filterVisibleElements);

  if (activeElement && filteredElements && filteredElements.length) {
    return activeElement === filteredElements[0];
  }

  return false;
};

/**
 *
 * @param {Event} e
 * @param {Element} element
 * @param {function} escCb
 */
export const commonPopupKeyDownEvent = (e = null, element = null, escCb = null) => {
  if (element && e) {
    if (escCb && isEventKey(e, KeyCode.esc)) {
      // looking for a nested popup
      const nestedPopup = !!(element && element.querySelector(".showView:not(.scrollToWrapper)"));
      if (!nestedPopup) {
        escCb();
      }
    } else if (isTryingExitElementByButtonTab(element, e)) {
      e.preventDefault();
    }
  }
};

/**
 * @param {NodeList} focusableElements
 * @param {boolean} preventScroll
 */
export const focusElement = (focusableElements = [], preventScroll) => {
  if (focusableElements && focusableElements.length) {
    let isElementWasFocused = false;

    const onSuitableElementEncountered = (el) => {
      el.focus({
        preventScroll,
      });

      if (!preventScroll && "scrollIntoView" in el) {
        el.scrollIntoView(true);
      }
    };

    for (const el of focusableElements) {
      if (
        el.getAttribute("aria-checked") === "true"
        || ((el.type === "radio" || el.type === "checkbox") && el.checked)
        || el.dataset?.focus === "true"
      ) {
        onSuitableElementEncountered(el);
        isElementWasFocused = true;
        break;
      }
    }

    if (!isElementWasFocused) {
      onSuitableElementEncountered(focusableElements[0]);
    }
  }
};
